import { addDays, addHours, addMonths, differenceInMonths, isAfter, isBefore, setHours, setMinutes } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import PropTypes from "prop-types";
import uuid from "uuid/v4";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Icon,
  Input,
  Message,
  Popup,
  Radio,
  Select,
} from "semantic-ui-react";

import "react-datepicker/dist/react-datepicker.css";
import { walmartMXTranslations } from "../../../translations/walmartMXTranslations";

import { Config } from "../../../config/api";
import { LangContext } from "../../../context";
import { checkIfWmx, getMaxAvailableEndDate, parseAPIDateTimeWithTimeZone } from "../../../libs/common_utils";
import { useDebounce, useForm } from "../../../libs/component_utils";
import { freqOptimizationEnabledByGoalType } from "../../../models/campaign";
import Pager from "../../../models/pager";
import AgenciesService from "../../../services/agencies";
import CampaignsService from "../../../services/campaign";
import CommonService from "../../../services/common";
import SiteListService from "../../../services/inventory/siteList";
import T1Service from "../../../services/t1";
import DecimalInput from "../../common/decimalInput";
import SitesFilter from "../../inventory/site-list/filter";
import { daily_pacing_options, spend_cap_automatic_options } from "../fixtures";
import DbaComponent from "../form/dba";
import FrequencyCapComponent from "../form/frequency-cap";
import GoalTypeComponent from "../form/goal";
import { BudgetFlight, budgetFlightsValidation } from "./budget-flight";
import BrandLiftPreSaveModal from "./BrandLiftPreSaveModal";
import BrandLiftSelectionModal from "./BrandLiftSelectionModal";
import BrandLiftDeactivateModal from "./BrandLiftDeactivateModal";
import BrandLiftQuestions from "./BrandLiftQuestions";
import useIsAdmin from "../../common/hooks/useIsAdmin";
import { MAX_CAMPAIGN_DURATION_MONTHS } from "../../../libs/consts";

function addFlight(flights, tz) {
	const newFlights = [...flights];
  const today = addHours(new Date(), 1);
	const initialStartDate = newFlights[0].start_date;
	const maxAvailableEndDate = getMaxAvailableEndDate(initialStartDate)
  const nextDate = addDays(setMinutes(setHours(today, 0), 0), 1);
  const lastFlightEndDate = addHours(newFlights[newFlights.length - 1].end_date, 1);
	const budgetFlightStart = isAfter(lastFlightEndDate, nextDate) ? lastFlightEndDate : nextDate;
	const presumedBudgetFlightEnd = addMonths(budgetFlightStart, 1);
	const budgetFlightEnd = isBefore(presumedBudgetFlightEnd, maxAvailableEndDate) ? presumedBudgetFlightEnd : maxAvailableEndDate;

	newFlights.push(
		{
			"start_date": budgetFlightStart,
			"end_date": budgetFlightEnd,
			"total_impression_budget": "",
			"total_budget": 0,
		}
	)

	return newFlights;
}

/**
 * Campaign create page component
 * @param history
 * @return {*}
 * @constructor
 */
const CampaignForm = ({
	initialData,
	onSubmit,
	onCancel,
	formType = "create",
	submitText = "Create Campaign",
}) => {
	const intl = useIntl();
	const t1Service = new T1Service();
	const agenciesService = new AgenciesService();
	const campaignsService = new CampaignsService();
	const commonService = new CommonService();
	const siteListService = new SiteListService();

	const lang = React.useContext(LangContext);
	const {
		whitelabelBrandLiftCampaign,
		whitelabelCampaignVendorAgreement,
		whitelabelSupplyPgFlag,
		whitelabelSupplyPmpFlag,
		whitelabelSupplyAudienceFlag,
		whitelabelSupplyOpenFlag,
		whitelabelShowMargin,
	} = useFlags();
	const isAdmin = useIsAdmin();
	const supplyLabels = {
		"whitelabelSupplyPgFlag": "Programmatic Guaranteed",
		"whitelabelSupplyPmpFlag": "PMP",
		"whitelabelSupplyAudienceFlag": lang["campaign"]["supply_type"],
		"whitelabelSupplyOpenFlag": "Open Supply",
	};

	const isPG = (campaignType) => campaignType.toLowerCase() === "pg";
	const isPMP = (campaignType) => campaignType.toLowerCase().includes("pmp");
	const isEdit = Boolean(formType.toLowerCase() === "edit");

	const [serverError, setServerError] = useState("");
	const [formSending, setFormSending] = useState(false);
	const [isBrandLiftModalOpen, setIsBrandLiftModalOpen] = useState(false);
	const [isBrandLiftSelectionModalOpen, setIsBrandLiftSelectionModalOpen] = useState(false);
	const [isBrandLiftDeactivateModalOpen, setIsBrandLiftDeactivateModalOpen] = useState(false);

	/**
	 * Save campaign data
	 * @return {Promise<void>}
	 */
	const confirmSubmit = async () => {
		setIsBrandLiftModalOpen(false);
		try {
			setFormSending(true);
			await onSubmit(values);
		} catch (e) {
			setServerError(e.error.message);
		} finally {
			setFormSending(false);
		}
	};

	const addCampaignForm = () => {
		const isBrandLiftEnabled = values.brand_lift === 1 || values.brand_lift === true;
		if (!isEdit && isBrandLiftEnabled) {
			setIsBrandLiftModalOpen(true);
			return;
		}
		confirmSubmit();
	};

	/**
	 * Check if merit_pixel_id field should be present
	 * @param {string} goal_type
	 * @returns {boolean}
	 */
	const shouldHaveMeritPixelId = (goal_type) => {
		return Boolean(["roi", "cpa"].includes(goal_type.toLowerCase()));
	};

	/**
	 * do a custom validation
	 * @param e
	 * @param {object} fields
	 */
	const postValidation = (e, fields) => {
		let errors = {};
		const isBrandLiftEnabled = values.brand_lift === 1 || values.brand_lift === true;

		if (isBrandLiftEnabled) {
			if (!fields.your_brand?.trim()) {
				errors.your_brand = walmartMXTranslations.brandLiftYourBrandError;
			}
			if (!fields.competitor_1?.trim()) {
				errors.competitor_1 = walmartMXTranslations.brandLiftCompetitor1Error;
			}
			const questionIds = fields.question_ids || [];
			if (questionIds.length < 3 || questionIds.length > 5) {
				errors.question_ids = walmartMXTranslations.brandLiftQuestionIdsError;
			}
		}

		if (!fields.advertiser_id || fields.advertiser_id < 1) {
			errors["advertiser_id"] = "Advertiser is a required field";
			errors.advertiser_id = intl.formatMessage({
				id: "ERROR_EMPTY_ADVERTISER_ID",
				defaultMessage: "Please select an advertiser.",
			});
		}

		if (
			shouldHaveMeritPixelId(fields.goal_type) &&
			(!fields.merit_pixel_id || fields.merit_pixel_id < 1)
		) {
			errors.merit_pixel_id = intl.formatMessage({
				id: "ERROR_EMPTY_MERIT_PIXEL_ID",
				defaultMessage: "Please select a Conversion Pixel.",
			});
		}

		// Validate total flight duration
		const budgetFlightFields = fields["budget_flights"] ?? [];
		const finalFlightDate = budgetFlightFields[budgetFlightFields.length - 1]["end_date"];
		const initialFlightDate = budgetFlightFields[0]["start_date"];
		const maxAvailableEndDate = getMaxAvailableEndDate(initialFlightDate);
		if (isAfter(finalFlightDate, maxAvailableEndDate)) {
			errors["budget_flight_duration"] = intl.formatMessage(
				{
					id: "ERROR_MAX_CAMPAIGN_DURATION_MONTHS",
					defaultMessage: "Campaign duration should not exceed 36 months",
				}
			);
		}

		const budgetFlightErrors = budgetFlightsValidation(fields, intl, isBrandLiftEnabled);
		const isBudgetFlightErrors = budgetFlightErrors.filter(e => e).length > 0;
		if(isBudgetFlightErrors) errors["budget_flights"] = budgetFlightErrors;

		return errors;
	};

	const {
		values,
		errors,
    onSwitch,
		updateValues,
		setErrors,
		onChange,
		onSubmit: handleSubmit,
		loadData,
	} = useForm(addCampaignForm, initialData, postValidation);
  const sortedData = initialData.budget_flights.sort((a, b) => a.start_date - b.start_date);
  const earliestDate = sortedData[0].start_date;
	const campaignStarted = isBefore(
		earliestDate,
		parseAPIDateTimeWithTimeZone(new Date(), initialData.time_zone)
	);
	const cannotBeEdited = isEdit && campaignStarted;
	const isBrandLiftEnabled = values.brand_lift === 1 || values.brand_lift === true;
	const isBrandLiftEdit = isEdit && isBrandLiftEnabled;
	const brandLiftCampaignStarted = isBrandLiftEnabled && Boolean(values.brand_lift_activated_at);
	// Once a Brand Lift campaign has been activated and then deactivated, the status
	// is locked and cannot be flipped back to Active.
	const isBrandLiftStatusLocked = isBrandLiftEdit && brandLiftCampaignStarted && !initialData.status;

	const handleStatusChange = (e, data) => {
		const changingToInactive = Number(data.value) === 0;
		const currentlyActive = Boolean(initialData.status);
		if (
			changingToInactive &&
			currentlyActive &&
			isBrandLiftEdit &&
			brandLiftCampaignStarted
		) {
			setIsBrandLiftDeactivateModalOpen(true);
			return;
		}
		onChange(e, data);
	};

	const confirmBrandLiftDeactivate = () => {
		onChange(null, { name: "status", value: 0 });
		setIsBrandLiftDeactivateModalOpen(false);
	};

	const [advertisers, setAdvertisers] = useState([]);
	const [currencies, setCurrencies] = useState([]);
	const [timeZones, setTimeZones] = useState([]);
	const [goalTypes, setGoalTypes] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [prevSiteLists, setPrevSiteLists] = useState([]);
	const [siteLists, setSiteLists] = useState([]);
	const [siteListsLoad, setSiteListsLoad] = useState(false);
	const [currencyRate, setCurrencyRate] = useState(null);

	/**
	 * check that we have all necessary goal types
	 * @param {array} loadedGoals
	 * @param {object} goal
	 */
	const verifyCorrectGoalType = (loadedGoals, goal) => {
		if (!loadedGoals.find((x) => x.value === goal)) {
			setServerError(
				intl.formatMessage({
					id: "ERROR_INVALID_GOAL_TYPE",
					defaultMessage:
						"You have saved a campaign with invalid goal type. This campaign cannot be properly saved",
				})
			);
		}
	};

	useEffect(() => {
		// load list of advertisers
		const agency = agenciesService.getSelectedAgency() || 0;

		(async () => {
			try {
				const [advertisersResp, currenciesResp, timeZonesResp, goalTypesResp] =
					await Promise.all([
						t1Service.advertisers(agency),
						commonService.getCurrencies(),
						commonService.getTimeZones(),
						commonService.getGoalTypes()
					]);

				setAdvertisers(
					advertisersResp.data.map(({ id, title }) => ({
						"key": id,
						"text": title,
						"value": id,
					}))
				);
				setCurrencies(
					currenciesResp.data.map(({ id, title, currency_code, symbol }) => ({
						"key": id,
						"text": `${title} (${currency_code})`,
						"value": currency_code,
						"symbol": symbol,
					}))
				);
				setTimeZones(
					timeZonesResp.data.map(({ id, title, value }) => ({
						"key": id,
						"text": title,
						"value": value,
					}))
				);
				setGoalTypes(
					goalTypesResp.data.map((item) => ({
						...item,
						"key": item.id,
						"text": item.title,
					}))
				);

				verifyCorrectGoalType(goalTypesResp.data, values.goal_type);
			} catch (e) {
				console.log(e);
			} finally {
				setIsLoading(false);
			}
		})();
	}, []);

	useEffect(() => {
		const isBrandLiftEnabled = values ? values.brand_lift === 1 || values.brand_lift === true : false;
		if (isBrandLiftEnabled) {
			const budgetFlights = values.budget_flights;
			updateValues({ "budget_flights": [budgetFlights[0]], ...(!isEdit && { "status": 0 }) })
		}
	}, [values.brand_lift])

	const BRAND_LIFT_VENDOR_ID = 1883; // ONLY FOR WALMART but using for ACME for testing purposes

	// TODO: The vendor agreement fetch endpoint (getVendorAgreement) is currently only configured
	// for Walmart's brand lift feature — BRAND_LIFT_VENDOR_ID is hardcoded to Walmart's vendor ID.
	// To fully enable the whitelabel-campaign-vendor-agreement feature for other clients, the
	// endpoint and vendor ID lookup will need to be generalized per client.

	// On create (walmart/acme + brand_lift on): seed vendor_contracts from the vendor API.
	// When brand_lift is off or client isn't walmart/acme: clear vendor_contracts.
	// Currency rate is fetched in both modes to display the chosen currency cost.
	useEffect(() => {
		const isBrandLiftEnabled = Boolean(values?.brand_lift);
		if (!isBrandLiftEnabled || !checkIfWmx()) {
			updateValues({ "vendor_contracts": [] });
			return;
		}

		if (values.currency) {
			setCurrencyRate(null);
			commonService.getCurrencyRate(values.currency)
				.then((rateResponse) => {
					const rawRate = rateResponse?.data?.rate;
					if (rawRate != null) {
						const parsedRate = Number(rawRate);
						if (!Number.isNaN(parsedRate)) setCurrencyRate(parsedRate);
					}
				})
				.catch(() => setCurrencyRate(null));
		}

		// Skip the vendor seed on edit (campaign already provides it) or when a contract row
		// already exists — otherwise a currency change would overwrite user edits to price /
		// use_mm_contract.
		if (isEdit || values.vendor_contracts?.length > 0) return;

		campaignsService.getVendorAgreement(BRAND_LIFT_VENDOR_ID)
			.then((vendorResponse) => {
				if (vendorResponse?.data) {
					const price = Math.round(vendorResponse.data.rate_card_price * 100) / 100;
					updateValues({ "vendor_contracts": [{ vendor_id: vendorResponse.data.id, vendor_name: vendorResponse.data.vendor_name, use_mm_contract: true, price, rate_card_type: "CPM" }] });
				}
			})
			.catch((err) => console.error("Failed to fetch vendor agreement:", err));
	}, [values.brand_lift, values.currency])

	const shouldDisableAddFlightButton = useMemo(() => {
		const isBrandLiftEnabled = values.brand_lift === 1 || values.brand_lift === true;
		const firstFlightStartDate = values.budget_flights[0].start_date;
		const lastFlightEndDate = values.budget_flights[values.budget_flights.length - 1].end_date;
		const monthDifference = differenceInMonths(lastFlightEndDate, firstFlightStartDate);
		return monthDifference >= MAX_CAMPAIGN_DURATION_MONTHS || isBrandLiftEnabled;
	}, [values.budget_flights, values.brand_lift]);

	const requestSiteLists = (filters, successCallback, errorCallback) => {
		const filter = new SitesFilter();
		filter.fromJson(filters);
		const pager = new Pager();
		pager.reset();

		siteListService
			.list({...filter.toRequestJson(), ...pager.toJson()})
			.then((response) => {
				const data = response.data.map((item) => ({
					"key": `${item.id} - ${item.title}`,
					"text": `${item.id} - ${item.title}`,
					"value": item.id
				}));
				successCallback(data);
			})
			.catch(() => {
				errorCallback();
			})
	}

	const loadSiteListOptions = useDebounce(Config.search_debounce_delay, (query) => {
		setSiteListsLoad(true);

		requestSiteLists(
			{ title: query, status: "active", id: "" },
			(data) => {
				setSiteLists(data);
				setSiteListsLoad(false);
			},
			() => {
				setSiteLists([]);
				setSiteListsLoad(false);
			}
		)
	});

	useEffect(() => loadSiteListOptions(""), []);
	useEffect(() => {
		const id = values.site_list_ids[0];
		if(!id) return;

		requestSiteLists(
			{ title: "", status: "all", id: id },
			(data) => setPrevSiteLists(data),
			() => setPrevSiteLists(data)
		)
	}, [values.site_list_ids[0]])

	// effect, to control correct state of frequency optimized
	useEffect(() => {
		if (
			!freqOptimizationEnabledByGoalType(values.goal_type) &&
			values.frequency_optimization === 1
		) {
			loadData({
				...values,
				"frequency_optimization": 0,
			});
		}
	}, [values.goal_type]);

	useEffect(() => {
		if (!isEdit && values.advertiser_id) {
			updateValues({ "merit_pixel_id": null });
		}
	}, [values.advertiser_id]);

	useEffect(() => {
		if(!whitelabelShowMargin && values.hasOwnProperty("margin_pct")) {
			updateValues({ "margin_pct": null });
		}
	}, [])

	useEffect(() => {
		if (isPG(values.campaign_type) && values.brand_lift) {
			updateValues({ "brand_lift": false, "question_ids": [], "your_brand": "", "competitor_1": "", "competitor_2": "", "competitor_3": "" });
		}
	}, [values.campaign_type]);

	/**
	 * handle start date change
	 * @param {Date} date
	 */
	const handleStartDateChange = (date) => {
		onChange(null, { "name": "start_date", "value": date });
	};

	/**
	 * Find advertiser by id
	 * @param {array} advertisers
	 * @param {number} selected_id
	 * @return {string}
	 */
	const getAdvertiserName = (advertisers, selected_id) => {
		return advertisers.find((a) => a.value === selected_id)?.text || "-";
	};

	/**
	 * Find timezone by id
	 * @param {array} zones
	 * @param {string} selected_id
	 * @return {string}
	 */
	const getTimeZoneName = (zones, selected_id) => {
		if (isLoading) {
			return "-";
		}
		return (
			zones.find((a) => a.value.toLowerCase() === selected_id.toLowerCase())
				?.text || selected_id
		);
	};

	/**
	 * Find currency by id
	 * @param {array} currencies
	 * @param {string} selected_id
	 * @return {string}
	 */
	const getCurrency = (currencies, selected_id) => {
		if (isLoading) {
			return "-";
		}
		return (
			currencies.find(
				(a) => a.value.toLowerCase() === selected_id.toLowerCase()
			)?.text || selected_id
		);
	};

	// second supply flags are disabled
	const secondSupplyFLagsAreDisabled =
		!whitelabelSupplyPmpFlag &&
		!whitelabelSupplyAudienceFlag &&
		!whitelabelSupplyOpenFlag;

	const generateCampaignTypeDropdown = () => {
		/**
		 * generate supply text based on LD flags
		 * @return {string}
		 */
		const getOptionText = () => {
			const option = [];
			if (whitelabelSupplyPmpFlag) {
				option.push(supplyLabels["whitelabelSupplyPmpFlag"]);
			}

			if (whitelabelSupplyAudienceFlag) {
				option.push(supplyLabels["whitelabelSupplyAudienceFlag"]);
			}

			if (whitelabelSupplyOpenFlag) {
				option.push(supplyLabels["whitelabelSupplyOpenFlag"]);
			}

			return option.join(" / ");
		};

		if (!secondSupplyFLagsAreDisabled && whitelabelSupplyPgFlag) {
			const options = [{ "text": "Programmatic Guaranteed", "value": "PG" }];
			options.push({
				"text": getOptionText(),
				"value": `${Config.strategy_supply_type.toUpperCase()}/PMP/OPEN`,
			});

			return (
				<Select
					selection
					options={options}
					value={values.campaign_type}
					name="campaign_type"
					aria-label="Campaign Type"
					onChange={onChange}
					className="input-field-wider"
				/>
			);
		} else {
			if (whitelabelSupplyPgFlag) {
				return "Programmatic Guaranteed";
			} else {
				return getOptionText();
			}
		}
	};

	const PG = isPG(values.campaign_type);
	const isPMPCampaign = isPMP(values.campaign_type);

	const siteListOptions = useMemo(() => {
		return [...prevSiteLists, ...siteLists].reduce((obj, item) => {
			obj[item.value] = item;
			return obj;
		}, {});
	}, [prevSiteLists, siteLists]);

  const selectedCurrencySymbol = useMemo(() => currencies.find((x) => x.value === values.currency)?.symbol, [currencies, values.currency]);
	const selectedSiteListId = values.site_list_ids[0];

	return (
		<>
			<Message
				style={{ "marginTop": "10px" }}
				error
				hidden={!serverError}
				size="tiny"
				content={serverError}
			/>

			{
				Config.public_client === "walmart" && <div style={{position: "absolute", right: 0, width: "35%"}}>
					<Message info>
						<p><span style={{"fontWeight": "bolder"}}>IMPORTANTE:</span> {walmartMXTranslations.campaignCreateCostText}</p>
						<ul>
							<li> {walmartMXTranslations.campaignCreateTechFeeText} </li>
							<li> {walmartMXTranslations.campaignCreateAudienceFeeText} </li>
							<li> {walmartMXTranslations.campaignCreateSalesFeeText}</li>
							{whitelabelBrandLiftCampaign && <li>{walmartMXTranslations.campaignCreateBrandliftText}</li>}
						</ul>
					</Message>
				</div>
			}
			<Form
				onSubmit={handleSubmit}
				size="small"
				loading={isLoading || formSending}
				noValidate
				error={Boolean(Object.keys(errors).length)}
				autoComplete="off"
			>
				<Form.Field inline disabled={(isBrandLiftEnabled && !isEdit) || isBrandLiftStatusLocked}>
					<label>
						{intl.formatMessage({
							id: "LABEL_STATUS",
							defaultMessage: "Status",
						})}
					</label>
					<Radio
						name="status"
						label={intl.formatMessage({
							id: "STATUS_ACTIVE",
							defaultMessage: "Active",
						})}
						value={1}
						checked={Boolean(values.status)}
						onChange={handleStatusChange}
					/>
					<Radio
						style={{ "marginLeft": "15px" }}
						name="status"
						label={intl.formatMessage({
							id: "STATUS_INACTIVE",
							defaultMessage: "Inactive",
						})}
						value={0}
						checked={!Boolean(values.status)}
						onChange={handleStatusChange}
					/>
				</Form.Field>

				<Form.Field
					required
					inline
					error={errors.hasOwnProperty("campaign_name")}
					disabled={isBrandLiftEdit}
				>
					<label>
						{intl.formatMessage({
							id: "LABEL_CAMPAIGN_NAME",
							defaultMessage: "Campaign Name",
						})}
					</label>
					<Input
						name="campaign_name"
						required
						maxLength="256"
						defaultValue={values.campaign_name}
						onBlur={onChange}
						className="input-field-wider"
						disabled={isBrandLiftEdit}
					/>
					<div className="custom-error">{errors["campaign_name"]}</div>
				</Form.Field>

				<Form.Field
					required={!isEdit}
					inline
					error={errors.hasOwnProperty("campaign_type")}
					disabled={isBrandLiftEdit}
				>
					<label>
						{intl.formatMessage({
							id: "LABEL_CAMPAIGN_TYPE",
							defaultMessage: "Campaign Type",
						})}
					</label>
					{isEdit ? (PG ? "Programmatic Guanranteed" : values.campaign_type ) : generateCampaignTypeDropdown()}
					<div className="custom-error">{errors["campaign_type"]}</div>
				</Form.Field>
				{whitelabelBrandLiftCampaign && isPMPCampaign && (
					<Form.Field inline disabled={isEdit}>
						<label>
							{intl.formatMessage({
								id: "LABEL_BRAND_LIFT",
								defaultMessage: "Brand Lift",
							})}?
						</label>
						<Radio
							name="brand_lift"
							label={intl.formatMessage({
								id: "LABEL_YES",
								defaultMessage: "Yes",
							})}
							value={1}
							checked={Boolean(values.brand_lift)}
							onChange={() => setIsBrandLiftSelectionModalOpen(true)}
						/>
						<Radio
							style={{ "marginLeft": "15px" }}
							name="brand_lift"
							label={intl.formatMessage({
								id: "LABEL_NO",
								defaultMessage: "No",
							})}
							value={0}
							checked={!Boolean(values.brand_lift)}
							onChange={onChange}
						/>
						<a
							href={require("../../../assets/client/walmart/brand_lift_user_guide.pdf")}
							target="_blank"
							rel="noopener noreferrer"
							style={{ marginLeft: "20px", pointerEvents: "auto", opacity: 1 }}
						>
							{walmartMXTranslations.brandLiftUserGuideLink}
						</a>
					</Form.Field>
				)}
				{whitelabelCampaignVendorAgreement && isAdmin && ["walmart", "acme"].includes(Config.public_client) && (
					<>
						<h4 className="ui header" style={{ opacity: isBrandLiftEdit ? "0.45" : "1" }}>
							{intl.formatMessage({ id: "LABEL_VENDOR_AGREEMENT", defaultMessage: "Vendor Agreement" })}
						</h4>
						<Divider />
						{values.vendor_contracts?.length > 0 && (
							<div className="budget-flight__row">
								<Form.Group widths="equal" style={{ flex: 1 }}>
									<Form.Field disabled={isBrandLiftEdit}>
										<label>{intl.formatMessage({ id: "LABEL_VENDOR_NAME", defaultMessage: "Vendor Name" })}</label>
										<Dropdown
											selection
											disabled={isBrandLiftEdit}
											options={[{ key: values.vendor_contracts[0].vendor_id, text: values.vendor_contracts[0].vendor_name, value: values.vendor_contracts[0].vendor_id }]}
											value={values.vendor_contracts[0].vendor_id}
											style={{ width: "100%" }}
										/>
									</Form.Field>
									<Form.Field disabled={isBrandLiftEdit}>
										<label>{intl.formatMessage({ id: "LABEL_INVOICE_TO", defaultMessage: "Invoice To" })}</label>
										<div style={{ display: "flex", alignItems: "center", gap: "12px", height: "38px" }}>
											<Radio
												label={intl.formatMessage({ id: "LABEL_CLIENT", defaultMessage: "Client" })}
												checked={values.vendor_contracts?.[0]?.use_mm_contract === false}
												onChange={() => updateValues({ "vendor_contracts": [{ ...values.vendor_contracts[0], use_mm_contract: false }] })}
												disabled={isBrandLiftEdit}
											/>
											<Radio
												label={intl.formatMessage({ id: "LABEL_MEDIAMATH", defaultMessage: "MediaMath" })}
												checked={values.vendor_contracts?.[0]?.use_mm_contract === true}
												onChange={() => updateValues({ "vendor_contracts": [{ ...values.vendor_contracts[0], use_mm_contract: true }] })}
												disabled={isBrandLiftEdit}
											/>
										</div>
									</Form.Field>
									<Form.Field disabled={isBrandLiftEdit}>
										<label>{intl.formatMessage({ id: "LABEL_CPM_COST_USD", defaultMessage: "CPM Cost (USD)" })}</label>
										<DecimalInput
											key={values.vendor_contracts?.[0]?.vendor_id ?? "empty"}
											label="$"
											type="number"
											min={0}
											step="0.01"
											places={2}
											disabled={isBrandLiftEdit}
											defaultValue={values.vendor_contracts?.[0]?.price ?? ""}
											onBlur={(e) => {
												const raw = e.target.value;
												const price = raw === "" ? "" : Number.parseFloat(raw);
												updateValues({ "vendor_contracts": [{ ...values.vendor_contracts[0], price }] });
											}}
										/>
									</Form.Field>
									<Form.Field disabled={isBrandLiftEdit}>
										<label>
											{intl.formatMessage({ id: "LABEL_CPM_COST", defaultMessage: "CPM Cost" })}
											{values.currency ? ` (${values.currency})` : ""}
										</label>
										<div style={{ height: "38px", display: "flex", alignItems: "center" }}>
											<span>
												{currencyRate != null && values.vendor_contracts?.[0]?.price !== "" && values.vendor_contracts?.[0]?.price != null && values.currency
													? new Intl.NumberFormat(undefined, { style: "currency", currency: values.currency }).format(values.vendor_contracts[0].price * currencyRate)
													: "—"}
											</span>
										</div>
									</Form.Field>
								</Form.Group>
								<div className="budget-flight__delete-control">
									{!isBrandLiftEnabled && (
										<Icon
											className="trash"
											onClick={() => {
												if (isBrandLiftEnabled) return;
												updateValues({ "vendor_contracts": [] });
											}}
										/>
									)}
								</div>
							</div>
						)}
						<Divider />
						{!isEdit && (
							<Form.Field align="right">
								<button
									className="ui button tiny secondary"
									type="button"
									disabled={values.vendor_contracts?.length > 0}
									onClick={() => {
										campaignsService.getVendorAgreement(BRAND_LIFT_VENDOR_ID)
											.then((response) => {
												if (response?.data) {
													const price = Math.round(response.data.rate_card_price * 100) / 100;
													updateValues({ "vendor_contracts": [{ vendor_id: response.data.id, vendor_name: response.data.vendor_name, use_mm_contract: true, price, rate_card_type: "CPM" }] });
												}
											})
											.catch((err) => console.error("Failed to fetch vendor agreement:", err));
									}}
								>
									{intl.formatMessage({ id: "LABEL_ADD_VENDOR_AGREEMENT", defaultMessage: "Add Agreement" })}
									<Icon style={{ marginLeft: "6px" }} className="plus circle" />
								</button>
							</Form.Field>
						)}
					</>
				)}
				<Form.Field
					required={!isEdit}
					inline
					error={errors.hasOwnProperty("advertiser_id")}
					disabled={isBrandLiftEdit}
				>
					<label>
						{intl.formatMessage({
							id: "LABEL_ADVERTISER",
							defaultMessage: "Advertiser",
						})}
					</label>
					{isEdit ? (
						getAdvertiserName(advertisers, values.advertiser_id)
					) : (
						<Dropdown
							search
							selection
							placeholder={intl.formatMessage({
								id: "HINT_ADVERTISER",
								defaultMessage: "Select advertiser",
							})}
							options={advertisers}
							name="advertiser_id"
							aria-label="Advertiser"
							value={values.advertiser_id}
							onChange={onChange}
						/>
					)}
					<div className="custom-error">{errors["advertiser_id"]}</div>
				</Form.Field>

				<Form.Field
					required={!cannotBeEdited}
					inline
					error={errors.hasOwnProperty("time_zone")}
					disabled={isBrandLiftEdit}
				>
					<label>
						{intl.formatMessage({
							id: "LABEL_TIME_ZONE",
							defaultMessage: "Time Zone",
						})}
					</label>
					{cannotBeEdited ? (
						getTimeZoneName(timeZones, values.time_zone)
					) : (
						<Select
							selection
							options={timeZones}
							loading={isLoading}
							disabled={(formType.toLowerCase() === "edit" && campaignStarted) || isBrandLiftEdit}
							value={values.time_zone}
							name="time_zone"
							aria-label="Time Zone"
							onChange={onChange}
							className="input-field-wider"
						/>
					)}
					<div className="custom-error">{errors["time_zone"]}</div>
				</Form.Field>

				<Form.Field inline required={!cannotBeEdited} disabled={isBrandLiftEdit}>
					<label style={{ "margin": "0 .85714286em 0 0" }}>
						{intl.formatMessage({
							id: "LABEL_CURRENCY",
							defaultMessage: "Currency",
						})}
					</label>
					{cannotBeEdited ? (
						getCurrency(currencies, values.currency)
					) : (
						<Select
							selection
							value={values.currency}
							options={currencies}
							loading={isLoading}
							onChange={onChange}
							name="currency"
							aria-label="Currency"
							disabled={isBrandLiftEdit}
						/>
					)}
				</Form.Field>

				<h4 className="ui header" style={{ opacity: isBrandLiftEdit ? "0.45": "1" }}>
					{intl.formatMessage({
						id: "LABEL_BUDGET_FLIGHTS",
						defaultMessage: "Budget Flights",
					})}
					{isBrandLiftEnabled && checkIfWmx() && (
						<span style={{ display: "inline-flex", alignItems: "center", marginLeft: "6px", fontSize: "1rem" }}>
							<Popup
								inverted
								content={walmartMXTranslations.brandLiftBudgetFlightsTooltip}
								size="mini"
								trigger={
									<Icon
										name="help circle"
										style={{ position: "relative" }}
										className="cursor-help"
									/>
								}
							/>
						</span>
					)}
				</h4>
				<Divider />
				{
					values["budget_flights"].map((_f, index) => (
						<BudgetFlight
							key={index}
							index={index}
							intl={intl}
							values={values}
							errors={errors}
							setErrors={setErrors}
							updateValues={updateValues}
							isPG={PG}
							isBrandLiftEnabled={isBrandLiftEnabled}
              currency={selectedCurrencySymbol}
							disabled={isBrandLiftEdit}
						/>
					))
				}
				<Divider />
				{
					!PG && <Form.Field align="right" error={errors?.["budget_flight_duration"]} disabled={isBrandLiftEdit}>
						<div className="custom-error" style={{ marginLeft: 0 }}>{errors?.["budget_flight_duration"]}</div>
						<button
							className="ui button tiny secondary"
							disabled={shouldDisableAddFlightButton || isBrandLiftEdit}
							onClick={() => {
								const newFlights = addFlight(values["budget_flights"], values["time_zone"]);
								updateValues({ ...values, "budget_flights": newFlights });
							}}
							type="button"
						>
							{intl.formatMessage({
								id: "LABEL_ADD_FLIGHT",
								defaultMessage: "Add Flight",
							})}
							<Icon style={{ marginLeft: "6px" }} className="plus circle" />
						</button>
					</Form.Field>
				}
				<GoalTypeComponent
					goalTypes={goalTypes}
					loading={isLoading}
					disabled={cannotBeEdited || isBrandLiftEdit}
					onChange={onChange}
					values={values}
					errors={errors}
					currency={currencies.find((x) => x.value === values.currency)?.symbol}
				/>
				{!PG && (
					<NonPGFieldsComponent
						currencies={currencies}
						onChange={onChange}
						errors={errors}
						values={values}
						disabled={isBrandLiftEdit}
					/>
				)}
				{
					whitelabelShowMargin && <Form.Field inline error={errors.hasOwnProperty("margin_pct")} disabled={isBrandLiftEdit} required>
						<label>
							{intl.formatMessage({
								id: "LABEL_MARGIN",
								defaultMessage: "Margin",
							})}
						</label>
						<DecimalInput
							name="margin_pct"
							required
							type="number"
							min={0}
							max={1000}
							step={0.0001}
							places={4}
							defaultValue={values.margin_pct}
							label="%"
							onBlur={onChange}
							disabled={isBrandLiftEdit}
						/>
						<div className="custom-error">{errors["margin_pct"]}</div>
					</Form.Field>
				}
				{
					!PG && <Form.Group inline widths={1}>
						<Form.Field disabled={isBrandLiftEdit}>
							<label>
								{intl.formatMessage({
									id: "LABEL_SITE_LISTS",
									defaultMessage: "Site List",
								})}
							</label>
							{
								values.site_list_ids.length <= 1 && <Dropdown
									search
									selection
									clearable
									loading={siteListsLoad}
									options={Object.values(siteListOptions)}
									value={selectedSiteListId || ""}
									placeholder={intl.formatMessage({
										id: "PLACEHOLDER_SITE_LISTS",
										defaultMessage: "Search Site List",
									})}
									onSearchChange={(e, data) => loadSiteListOptions(data.searchQuery)}
									selectOnNavigation={false}
									selectOnBlur={false}
									disabled={isBrandLiftEdit}
									onChange={(e, data) => {
										const list = data.value && data.value !== "" ? [data.value] : [];
										updateValues({ site_list_ids: list }) }
									}
								/>
							}
							{
								values.site_list_ids.length > 1 && <div style={{float: "right"}}>
									<Message negative>
										{intl.formatMessage({
											id: "SITE_LIST_CAMPAIGN_SELECTION_NOTICE",
											defaultMessage: "Multiple Site Lists not supported",
										})}
									</Message>
								</div>
							}
						</Form.Field>
					</Form.Group>
				}
				{whitelabelBrandLiftCampaign && Boolean(values.brand_lift) && (
					<BrandLiftQuestions
						values={values}
						errors={errors}
						onChange={onChange}
						updateValues={updateValues}
						disabled={isEdit}
					/>
				)}
				<Divider hidden />
				<Divider hidden />
				<Divider hidden />
				<Form.Field align="right" className="create-controls">
					<Button size="tiny" type="button" onClick={() => onCancel(values)}>
						{intl.formatMessage({
							id: "BTN_CANCEL",
							defaultMessage: "Cancel",
						})}
					</Button>
					<Button size="tiny" className="create" type="submit">
						{submitText}
					</Button>
				</Form.Field>
			</Form>
			<BrandLiftPreSaveModal
				isOpen={isBrandLiftModalOpen}
				onClose={() => setIsBrandLiftModalOpen(false)}
				onConfirm={confirmSubmit}
			/>
			<BrandLiftSelectionModal
				isOpen={isBrandLiftSelectionModalOpen}
				onClose={() => setIsBrandLiftSelectionModalOpen(false)}
				onConfirm={() => {
					onChange(null, { name: "brand_lift", value: 1 });
					setIsBrandLiftSelectionModalOpen(false);
				}}
			/>
			<BrandLiftDeactivateModal
				isOpen={isBrandLiftDeactivateModalOpen}
				onClose={() => setIsBrandLiftDeactivateModalOpen(false)}
				onConfirm={confirmBrandLiftDeactivate}
			/>
		</>
	);
};

CampaignForm.propTypes = {
	initialData: PropTypes.object.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	formType: PropTypes.string,
	submitText: PropTypes.string,
}

const NonPGFieldsComponent = ({ currencies, onChange, errors, values, disabled = false }) => {
	const intl = useIntl();
  const { whitelabelCampaignBidOnCrossDevice = false } = useFlags();
  const alwaysBidOnCrossDevice = useMemo(() => whitelabelCampaignBidOnCrossDevice ?? false, []);

	return (
		<>
			<Form.Group inline>
				<Form.Field required disabled={disabled}>
					<label>
						{intl.formatMessage({
							id: "LABEL_DAILY_PACING",
							defaultMessage: "Daily Pacing",
						})}
					</label>
				</Form.Field>
				<Form.Field disabled={disabled}>
					<Select
						fluid
						name="spend_cap_type"
						aria-label="Daily Pacing"
						onChange={onChange}
						options={daily_pacing_options(intl)}
						value={values.spend_cap_type}
					/>
				</Form.Field>
				<Form.Field disabled={disabled || values.spend_cap_type === "no-limit"}>
					<Select
						name="spend_cap_automatic"
						options={spend_cap_automatic_options(intl)}
						onChange={onChange}
						value={values.spend_cap_automatic}
					/>
				</Form.Field>
				<Form.Field
					error={errors.hasOwnProperty("spend_cap_amount")}
					disabled={
						disabled ||
						values.spend_cap_type === "no-limit" ||
						Boolean(values.spend_cap_automatic)
					}
				>
					<DecimalInput
						name="spend_cap_amount"
						type="number"
						min={1}
						max={9999999.99}
						step={0.01}
						places={2}
						required={
							!Boolean(values.spend_cap_automatic) &&
							values.spend_cap_type !== "no-limit"
						}
						autoComplete="off"
						defaultValue={values.spend_cap_amount}
						onBlur={onChange}
						label={
							!currencies.length
								? "X"
								: currencies.find((x) => x.value === values.currency).symbol
						}
					/>{" "}
					{intl.formatMessage({
						id: "LABEL_PER_DAY",
						defaultMessage: "per Day",
					})}
					<div className="custom-error" style={{ "marginLeft": "0" }}>
						{errors["spend_cap_amount"]}
					</div>
				</Form.Field>
			</Form.Group>

			<DbaComponent
				onChange={onChange}
				values={values}
				disabled={disabled}
			/>

			<FrequencyCapComponent
				onChange={onChange}
				values={values}
				errors={errors}
				disabled={disabled}
			/>

			{!alwaysBidOnCrossDevice && <Form.Field inline disabled={disabled}>
				<label>
					{intl.formatMessage({
						id: "LABEL_BID_ON_CROSS_DEVICE",
						defaultMessage: "Bid on Cross Device",
					})}
				</label>
				<Radio
					name="restrict_targeting_to_same_device_id"
					label={intl.formatMessage({
						id: "LABEL_ACTIVE",
						defaultMessage: "Active",
					})}
					value={0}
					checked={!Boolean(values.restrict_targeting_to_same_device_id)}
					onChange={onChange}
				/>
				<Radio
					style={{ "marginLeft": "15px" }}
					name="restrict_targeting_to_same_device_id"
					label={intl.formatMessage({
						id: "LABEL_INACTIVE",
						defaultMessage: "Inactive",
					})}
					value={1}
					checked={Boolean(values.restrict_targeting_to_same_device_id)}
					onChange={onChange}
				/>
			</Form.Field>}
		</>
	);
};
NonPGFieldsComponent.propTypes = {
	"currencies": PropTypes.array.isRequired,
	"values": PropTypes.object.isRequired,
	"onChange": PropTypes.func.isRequired,
	"errors": PropTypes.object.isRequired,
	"disabled": PropTypes.bool,
};

export default CampaignForm;
